import apiClient from './apiClient';
import {
  Denuncia,
  DenunciaDetalhe,
  DenunciaEstatisticas,
  DenunciaAndamento,
  ConfigDoCanal,
  DadosDaDenunciaPublica,
  AcompanhamentoDoRelator,
  DenunciaStatus,
  DenunciaCategoria,
  DenunciaPrioridade,
} from '../types/denuncia';

const extrair = (resposta: any) => resposta?.data ?? resposta;

class DenunciaService {
  private base = '/denuncias';

  // ── Público (anônimo) ──

  async abrirCanal(token: string): Promise<{ empresa: string }> {
    return extrair(await apiClient.get(`${this.base}/publico/${token}`));
  }

  async enviarDenuncia(token: string, dados: DadosDaDenunciaPublica): Promise<{ protocolo: string }> {
    return extrair(await apiClient.post(`${this.base}/publico/${token}`, dados));
  }

  async acompanhar(protocolo: string): Promise<AcompanhamentoDoRelator> {
    return extrair(await apiClient.get(`${this.base}/protocolo/${encodeURIComponent(protocolo)}`));
  }

  async enviarMensagem(protocolo: string, texto: string): Promise<void> {
    await apiClient.post(`${this.base}/protocolo/${encodeURIComponent(protocolo)}/mensagem`, { texto });
  }

  async anexarPorProtocolo(protocolo: string, arquivo: File): Promise<void> {
    const fd = new FormData();
    fd.append('file', arquivo);
    await apiClient.post(`${this.base}/protocolo/${encodeURIComponent(protocolo)}/anexo`, fd);
  }

  async anexarPeloComite(id: number, arquivo: File): Promise<void> {
    const fd = new FormData();
    fd.append('file', arquivo);
    await apiClient.post(`${this.base}/${id}/anexo`, fd);
  }

  async baixarAnexo(id: number, anexoId: number): Promise<Blob> {
    const resposta = await apiClient.get(`${this.base}/${id}/anexos/${anexoId}/download`, { responseType: 'blob' });
    return resposta as unknown as Blob;
  }

  // ── Interno (comitê) ──

  async listar(filtros?: { status?: DenunciaStatus; categoria?: DenunciaCategoria }): Promise<Denuncia[]> {
    return extrair(await apiClient.get(this.base, { params: filtros }));
  }

  async estatisticas(): Promise<DenunciaEstatisticas> {
    return extrair(await apiClient.get(`${this.base}/estatisticas`));
  }

  async obter(id: number): Promise<DenunciaDetalhe> {
    return extrair(await apiClient.get(`${this.base}/${id}`));
  }

  async adicionarAndamento(
    id: number,
    entrada: { tipo: 'NOTA' | 'MENSAGEM' | 'PEDIDO_INFO'; texto: string; visivelAoRelator: boolean }
  ): Promise<DenunciaAndamento> {
    return extrair(await apiClient.post(`${this.base}/${id}/andamentos`, entrada));
  }

  async mudarStatus(id: number, status: DenunciaStatus, texto?: string): Promise<Denuncia> {
    return extrair(await apiClient.patch(`${this.base}/${id}/status`, { status, texto }));
  }

  async atribuir(id: number, responsavelId: number | null): Promise<Denuncia> {
    return extrair(await apiClient.patch(`${this.base}/${id}/responsavel`, { responsavelId }));
  }

  async definirPrioridade(id: number, prioridade: DenunciaPrioridade | null): Promise<Denuncia> {
    return extrair(await apiClient.patch(`${this.base}/${id}/prioridade`, { prioridade }));
  }

  async gerarAcaoNoPlano(id: number): Promise<{ actionPlanItemId: number }> {
    return extrair(await apiClient.post(`${this.base}/${id}/plano-de-acao`));
  }

  async obterConfig(): Promise<ConfigDoCanal> {
    return extrair(await apiClient.get(`${this.base}/config`));
  }

  async configurar(ativo: boolean): Promise<ConfigDoCanal> {
    return extrair(await apiClient.put(`${this.base}/config`, { ativo }));
  }
}

const denunciaService = new DenunciaService();
export default denunciaService;
